import { faArrowRight, faCircleCheck } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { motion } from "framer-motion";

import { Brale } from "~/components/logos/brale";
import { CTAPrimary } from "~/components/site/call-to-action-primary";
import { container, item, Layout } from "~/components/site/layout";
import { BG_BASE_COLORS } from "~/lib/colors";
import { ROUTE_DATA } from "~/lib/constants";
import type { Stablecoin } from "~/lib/ecosystem";
import { STABLECOINS } from "~/lib/ecosystem";
import { getMeta } from "~/lib/get-meta";
import { getUrls } from "~/lib/get-urls";

const { desc, path, title } = ROUTE_DATA.product;

export type Content = Readonly<{
  title: string;
  text: string;
}>;

const ysbc: Stablecoin = {
  id: "ysbc",
  name: "Your Stablecoin",
  ticker: "YSBC",
  icon: "/assets/stablecoins/default-logo.png",
  borderColor: "hover:border-[#858ea1]",
  url: "/pricing",
  braleIssued: true,
};

const stablecoins = [...STABLECOINS, ysbc];

const defiEcosystem: Content[] = [
  {
    title: "Stablecoins",
    text: "Swap between your stablecoin and USDC and USDP.",
  },
  {
    title: "Blockchains",
    text: "Launch on 10+ blockchains and swap supply between them.",
  },
  {
    title: "Wallets",
    text: "Instant compatibility with leading EVM and non-EVM wallets.",
  },
  {
    title: "Exchanges",
    text: "Make stablecoins available on Uniswap, Orca, and aggregators. Support institutional access through BitGo and 1Konto.",
  },
  {
    title: "Payments",
    text: "Support real-time offramps through Coinflow and spend stablecoins anywhere through Rain Cards.",
  },
  {
    title: "Liquidity",
    text: "Liquidity is supported through Keyrock, 1Konto, and a network of liquidity providers.",
  },
  {
    title: "Custodians",
    text: "Custody your stablecoins with BitGo, a qualified custodian.",
  },
  {
    title: "Lenders",
    text: "Support custom lending markets with Euler Finance.",
  },
];

const cefiEcosystem: Content[] = [
  {
    title: "ACH",
    text: "Mint and Redeem with ACH directly at Brale.",
  },
  {
    title: "Wire",
    text: "Support large transactions through wire transfer.",
  },
  {
    title: "On-chain",
    text: "Convert into and out of your stablecoin with USDC and USDP.",
  },
];

export function loader({ request }: LoaderFunctionArgs) {
  return {
    urls: getUrls({ path, request }),
  };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return data ? getMeta({ desc, title, urls: data.urls }) : [];
};

export default function Product(): JSX.Element {
  return (
    <Layout>
      <section className="relative isolate overflow-clip">
        <div className="container section-first">
          <motion.div variants={item} className="mx-auto flex-shrink-0 lg:mx-0">
            <h1 className="eyebrow">Product</h1>
            <h2 className="mt-0 mb-6">
              Everything you need to launch a stablecoin
            </h2>
            <p className="subheading mt-2 mb-12">
              Brale removes the technical, regulatory, and operational
              complexity to issue a stablecoin.
            </p>
            <div className="flex gap-4">
              <a href="/contact" className="btn primary no-underline">
                Talk to sales
              </a>
              <a href="/pricing" className="btn secondary no-underline">
                See pricing
              </a>
            </div>
          </motion.div>
        </div>
      </section>
      <section className="section-sub">
        <div className="container">
          <div className="flex flex-col gap-36">
            <div className="grid lg:grid-cols-2 items-center max-w-none gap-x-16 gap-y-12 sm:gap-y-16 lg:gap-y-36">
              <motion.div variants={item}>
                <div className="eyebrow">Process</div>
                <h2 className="text-5xl mt-0 mb-6">Create your stablecoin</h2>
                <p className="subheading mt-2 mb-12">
                  Launch audited assets on 10+ blockchains in less than an hour
                </p>
                <ul className="m-0 p-0 space-y-3">
                  <li className="flex items-center gap-x-3 text-base md:text-lg">
                    <FontAwesomeIcon
                      className="h-6 w-6 text-green-500"
                      icon={faCircleCheck}
                    />
                    Decide what to call your stablecoin
                  </li>
                  <li className="flex items-center gap-x-3 text-base md:text-lg">
                    <FontAwesomeIcon
                      className="h-6 w-6 text-green-500"
                      icon={faCircleCheck}
                    />
                    Upload your logo & select your preferred blockchain
                  </li>
                  <li className="flex items-center gap-x-3 text-base md:text-lg">
                    <FontAwesomeIcon
                      className="h-6 w-6 text-green-500"
                      icon={faCircleCheck}
                    />
                    Deploy your new stablecoin
                  </li>
                </ul>
              </motion.div>
              <motion.img
                variants={item}
                src="/assets/site/create-ysbc-stablecoin.gif"
                alt="Brale Dashboard"
                className="round lg:sticky top-16 mb-16 md:mb-0 border border-primary"
              />
              <motion.div variants={item}>
                <h2 className="mt-0 mb-6">List on Brale&apos;s exchange</h2>
                <p className="subheading mt-2 mb-12">
                  Make your stablecoin available on Brale&apos;s regulated
                  exchange
                </p>
                <ul className="m-0 p-0 space-y-3">
                  <li className="flex items-center gap-x-3 text-base md:text-lg">
                    <FontAwesomeIcon
                      className="h-6 w-6 text-green-500"
                      icon={faCircleCheck}
                    />
                    Mint & redeem with USD or USDC
                  </li>
                  <li className="flex items-center gap-x-3 text-base md:text-lg">
                    <FontAwesomeIcon
                      className="h-6 w-6 text-green-500"
                      icon={faCircleCheck}
                    />
                    No fees or minimums
                  </li>
                  <li className="flex items-center gap-x-3 text-base md:text-lg">
                    <FontAwesomeIcon
                      className="h-6 w-6 text-green-500"
                      icon={faCircleCheck}
                    />
                    Access a network of market makers, custodians, and OTCs
                  </li>
                </ul>
              </motion.div>
              <motion.img
                variants={item}
                src="/assets/site/swap-stablecoins.gif"
                alt="Brale Dashboard"
                className="round lg:sticky top-16 mb-16 md:mb-0 border border-primary"
              />
              <motion.div variants={item}>
                <h2 className="mt-0 mb-6">Regulated reserves</h2>
                <p className="subheading mt-2 mb-12">
                  Brale manages the reserves, so you don&apos;t have to
                </p>
                <ul className="m-0 p-0 space-y-3">
                  <li className="flex items-center gap-x-3 text-base md:text-lg">
                    <FontAwesomeIcon
                      className="h-6 w-6 text-green-500"
                      icon={faCircleCheck}
                    />
                    Regulated reserve management & reporting
                  </li>
                  <li className="flex items-center gap-x-3 text-base md:text-lg">
                    <FontAwesomeIcon
                      className="h-6 w-6 text-green-500"
                      icon={faCircleCheck}
                    />
                    Built in daily reporting practices
                  </li>
                  <li className="flex items-center gap-x-3 text-base md:text-lg">
                    <FontAwesomeIcon
                      className="h-6 w-6 text-green-500"
                      icon={faCircleCheck}
                    />
                    Independently audited
                  </li>
                </ul>
              </motion.div>
              <motion.div
                variants={item}
                className="overflow-clip py-[3%] bg-black round lg:sticky top-16 mb-16 md:mb-0 border border-primary"
              >
                <img
                  src="/assets/site/regulated-reserves.png"
                  alt="Brale Dashboard"
                  className="w-full"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      <section className="section-sub">
        <div className="container">
          <motion.div
            variants={item}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className="eyebrow">DeFi Ecosystem</div>
            <h2 className="mt-0 mb-16">Works with the ecosystem</h2>
          </motion.div>
          <div className="flex flex-col col-span-2 gap-y-8">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="flex flex-col sm:grid sm:grid-cols-2 lg:grid-cols-4 blurb-gap"
            >
              {defiEcosystem.map((c, i) => (
                <motion.div key={c.title} variants={item}>
                  <h4 className="border border-primary mt-0 mb-4 items-center gap-3 px-6 py-2 rounded-full inline-flex bg-secondary">
                    <div
                      className={`rounded-full h-2 w-2 ${
                        BG_BASE_COLORS[(i + 1) % BG_BASE_COLORS.length]
                      }`}
                    ></div>
                    {c.title}
                  </h4>
                  <div className="blurb">{c.text}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
      <section className="section-sub">
        <div className="container">
          <motion.div
            variants={item}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className="eyebrow">CeFi Compatible</div>
            <h2 className="text-5xl mt-0 mb-16">
              Compatible with traditional payments
            </h2>
          </motion.div>
          <div className="flex flex-col col-span-2 gap-y-8">
            <motion.div
              variants={container}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="flex flex-col sm:grid sm:grid-cols-3 blurb-gap"
            >
              {cefiEcosystem.map((c, i) => (
                <motion.div variants={item} key={c.title}>
                  <h4 className="border border-primary mt-0 mb-4 text-xl items-center gap-3 px-6 py-2 rounded-full inline-flex bg-secondary">
                    <div
                      className={`rounded-full h-2 w-2 ${
                        BG_BASE_COLORS[(i + 9) % BG_BASE_COLORS.length]
                      }`}
                    ></div>
                    {c.title}
                  </h4>
                  <div className="blurb">{c.text}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
      <section className="section-sub">
        <div className="container">
          <motion.div
            variants={item}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className="eyebrow">Timeline</div>
            <h2 className="mt-0 mb-16">
              Launch{" "}
              <span className="line-through text-secondary">in years</span>{" "}
              today
            </h2>
          </motion.div>
          <motion.img
            variants={item}
            src="/assets/site/stablecoin-issuance-gantt-chart.svg"
            alt="Stablecoin issuance timeline graphic"
            className="round bg-gray-900 border border-primary"
          />
        </div>
      </section>
      <section className="section-sub">
        <div className="container">
          <motion.div
            variants={item}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className="eyebrow">Stablecoins</div>
            <h2 className="mt-0 mb-16">
              Build alongside the leading stablecoins
            </h2>
          </motion.div>
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 lg:gap-8"
          >
            {stablecoins
              .sort((a, b) => {
                if (a.ticker.toLowerCase() === "ysbc") return 1;
                if (b.ticker.toLowerCase() === "ysbc") return -1;
                if (a.braleIssued !== b.braleIssued) {
                  return b.braleIssued ? 1 : -1;
                }
                return a.ticker.localeCompare(b.ticker);
              })
              .map((token) => (
                <motion.a
                  variants={item}
                  key={token.ticker}
                  href={token.url}
                  className={`${token.borderColor} global-hover-transition round p-6 bg-secondary no-underline border border-primary group`}
                >
                  <div className="flex justify-between items-center">
                    <h3 className="m-0">{token.ticker}</h3>
                    <span className="inline-flex items-center rounded-full border border-primary px-3 py-1 text-sm font-medium text-black dark:text-white bg-gray-100 dark:bg-gray-900">
                      {token.name}
                    </span>
                  </div>
                  <div className="py-24">
                    <img
                      src={token.icon}
                      alt={`${token.name} (${token.ticker}) logo`}
                      className="max-w-36 !m-auto border border-primary rounded-full"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex-grow">
                      {token.braleIssued && (
                        <div className="flex gap-4 items-center">
                          <Brale height="24" width="24" />
                          <span>Brale Issued</span>
                        </div>
                      )}
                    </div>
                    <span className="p-2 border border-primary rounded-full opacity-0 group-hover:opacity-100 group-hover:translate-x-[-10px] relative -right-[10px] transition-all duration-500 leading-none">
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className="h-4 w-4"
                      />
                    </span>
                  </div>
                </motion.a>
              ))}
          </motion.div>
        </div>
      </section>
      <motion.div
        variants={container}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <motion.div variants={item}>
          <CTAPrimary
            heading="Create a stablecoin with Brale"
            subheading="Collaborate, plan, and build with us."
            primaryBtn="Talk to sales"
            primaryBtnUrl="/contact"
            secondaryBtn="See pricing"
            secondaryBtnUrl="/pricing"
          />
        </motion.div>
      </motion.div>
    </Layout>
  );
}
